import { Clock, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { formatTime } from '@/lib/utils'
import type { PlannedMeal } from '@/types/meal-plan'
import type { DayPlan } from './meal-planning-utils'
import { formatDutchDate } from './meal-planning-utils'

interface SelectedDayDetailProps {
  selectedDay?: DayPlan
  selectedDayMeals: PlannedMeal[]
  onRemoveMeal: (mealId: string) => Promise<boolean>
}

export function SelectedDayDetail({ selectedDay, selectedDayMeals, onRemoveMeal }: SelectedDayDetailProps) {
  return (
    <Card>
      <CardHeader className="space-y-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle>Diner voor {selectedDay?.label.toLowerCase()}</CardTitle>
            <p className="mt-1 text-sm text-gray-600">
              {selectedDay ? formatDutchDate(selectedDay.date, { weekday: 'long', day: 'numeric', month: 'long' }) : ''}
            </p>
          </div>
          {selectedDay?.isToday ? (
            <span className="rounded-full bg-orange-100 px-2 py-1 text-xs font-medium text-orange-800">Vandaag</span>
          ) : null}
        </div>
      </CardHeader>
      <CardContent>
        {selectedDayMeals.length ? (
          <div className="space-y-3">
            {selectedDayMeals.map((meal) => (
              <div key={meal.id} className="rounded-xl border border-gray-200 bg-white p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="break-words text-base font-semibold text-gray-900">
                      {meal.recipe?.title ?? 'Onbekend recept'}
                    </div>
                    {meal.recipe?.description ? <div className="mt-1 text-sm text-gray-600">{meal.recipe.description}</div> : null}
                    <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-gray-500">
                      {meal.recipe && (meal.recipe.prep_time || meal.recipe.cook_time) ? (
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {formatTime((meal.recipe.prep_time || 0) + (meal.recipe.cook_time || 0))}
                        </span>
                      ) : null}
                      {meal.planned_servings ? (
                        <span className="flex items-center gap-1">
                          <Users className="h-4 w-4" />
                          {meal.planned_servings} porties
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => void onRemoveMeal(meal.id)}>
                    Verwijderen
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-8 text-center text-gray-600">
            Nog geen diner gepland voor deze dag.
          </div>
        )}
      </CardContent>
    </Card>
  )
}
